import React, { useState } from "react";
import {
    Alert,
    Button,
    Card,
    Flex,
    Image,
    Skeleton,
    Text,
    rem,
} from "@mantine/core";
import useAnalytics from "../hooks/useAnalytics";
import { downloadCardImages } from "../utils/downloadCardImage";
import CSMLCTA from "./CSMLCTA";
import ReachabilityContact from "./ReachabilityContact";
import { useGetPlansQuery } from "../services/api";

interface CardProps {
    data?: string | null | undefined;
    source?: "OnBase" | "dynamic";
    isLoading: boolean;
}

interface CardsProps {
    data: {
        front?: Omit<CardProps, "isLoading">;
        back?: Omit<CardProps, "isLoading">;
        pdf?: number;
    };
    isLoading: boolean;
}

function IDCard(props: CardProps) {
    const { data, source, isLoading } = props;

    return (
        <Flex
            direction={"column"}
            align={!data ? "" : "flex-end"}
            w={"100%"}
            maw={rem(550)}
        >
            <Skeleton
                maw={"100%"}
                sx={{
                    aspectRatio: !isLoading ? "" : "8 / 5",
                }}
                width={!isLoading ? "" : "100%"}
                visible={isLoading}
            />
            {data ? (
                <Card shadow={"sm"} p={"sm"}>
                    <Image src={data} />
                </Card>
            ) : (
                !isLoading && (
                    <Card
                        sx={{
                            aspectRatio: "8 / 5",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        display="flex"
                        w={"100%"}
                        maw={rem(550)}
                        shadow={"sm"}
                        p="sm"
                    >
                        <Text>Failed loading ID Card</Text>
                    </Card>
                )
            )}
            {source === "dynamic" ? (
                <Text
                    mt={"xs"}
                    mr={"xs"}
                    fs="italic"
                    color={"cs-gray.400"}
                    fz={"xs"}
                >
                    This ID card was dynamically generated
                </Text>
            ) : null}
        </Flex>
    );
}

const DOWNLOAD_ERROR_MESSAGES: Record<string, string> = {
    NO_IMAGE_DATA:
        "No ID card image is available to download right now.",
    INVALID_IMAGE_DATA:
        "The ID card image could not be prepared for download.",
    UNKNOWN_ERROR:
        "Something went wrong while downloading the ID card. Please try again.",
};

export default function Cards(props: CardsProps) {
    const { data, isLoading } = props;
    const { trackEvent, AnalyticsEvents } = useAnalytics();
    const [downloadState, setDownloadState] = useState<"idle" | "loading">(
        "idle"
    );
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const { data: plans, isLoading: plansIsLoading } = useGetPlansQuery({});
    const productMarket = plans?.[0]?.productMarket;
    const activationUrl = plans?.[0]?.csmlActivationUrl;

    // Rendered whenever loading has finished, even if metadata ended up
    // missing/incomplete, so the "Card Metadata Missing" negative path can
    // surface its own inline error on click instead of the button silently
    // disappearing.
    const showDownloadButton = !isLoading;

    async function handleDownloadClick() {
        setDownloadError(null);
        setDownloadState("loading");
        trackEvent(AnalyticsEvents.CARD_DOWNLOAD_CLICKED, {
            has_front: !!data?.front?.data,
            has_back: !!data?.back?.data,
        });

        const result = await downloadCardImages({
            front: data?.front?.data,
            back: data?.back?.data,
        });

        setDownloadState("idle");

        if (result.success) {
            trackEvent(AnalyticsEvents.CARD_DOWNLOAD_SUCCESS, {
                method: result.method,
            });
        } else if (result.error === "USER_CANCELLED") {
            trackEvent(AnalyticsEvents.CARD_DOWNLOAD_CANCELLED, {});
        } else {
            setDownloadError(
                DOWNLOAD_ERROR_MESSAGES[result.error] ||
                    DOWNLOAD_ERROR_MESSAGES.UNKNOWN_ERROR
            );
            trackEvent(AnalyticsEvents.CARD_DOWNLOAD_FAILURE, {
                reason: result.error,
            });
        }
    }

    return (
        <>
            <Flex
                justify={"space-evenly"}
                align={{ base: "center", xl: "flex-start" }}
                direction={{ base: "column", xl: "row" }}
                gap={"lg"}
            >
                <IDCard
                    isLoading={isLoading}
                    source={data?.front?.source}
                    data={data?.front?.data}
                />
                <IDCard
                    isLoading={isLoading}
                    source={data?.back?.source}
                    data={data?.back?.data}
                />
            </Flex>
            {showDownloadButton && (
                <Button
                    fullWidth
                    mt={"xl"}
                    loading={downloadState === "loading"}
                    onClick={handleDownloadClick}
                    data-testid="download-id-card-button"
                >
                    Download ID Card
                </Button>
            )}
            {downloadError && (
                <Alert
                    color="red"
                    title="Download failed"
                    mt={"sm"}
                    data-testid="download-id-card-error"
                >
                    {downloadError}
                </Alert>
            )}
            {data?.pdf && (
                <Button
                    component="a"
                    fullWidth
                    mt={"xl"}
                    target="_blank"
                    href={`/api/member/id/${data?.pdf}?inline=true&type=application%2Fpdf&filename=${data?.pdf}.pdf`}
                >
                    Printable PDF
                </Button>
            )}
            {!isLoading && <ReachabilityContact />}
            {!isLoading && !plansIsLoading && productMarket && (
                <CSMLCTA
                    productMarket={productMarket}
                    activationUrl={activationUrl}
                />
            )}
        </>
    );
}
